import type { KnowledgeUnit, KnowledgeProvider, UnitMeta, UnitType } from './types'

// ---------------------------------------------------------------------------
// FNV-1a 32-bit hash — deterministic, pure (no Date/random).
// Used to produce a stable version fingerprint from sorted id@version pairs.
// ---------------------------------------------------------------------------
function fnv1a(str: string): string {
  let h = 2166136261 // FNV offset basis
  for (let i = 0; i < str.length; i++) {
    // charCodeAt returns a number; XOR + imul + >>> 0 keeps us in uint32.
    h = Math.imul(h ^ str.charCodeAt(i), 16777619) >>> 0
  }
  return h.toString(16)
}

// ---------------------------------------------------------------------------
// createInMemoryKnowledge
// ---------------------------------------------------------------------------

export function createInMemoryKnowledge(units: KnowledgeUnit[]): KnowledgeProvider {
  // O(1) id lookup; built once at construction time.
  const byId = new Map<string, KnowledgeUnit>(units.map((u) => [u.id, u]))

  // Compute the version hash once — stable across repeated calls.
  const versionHash: string = (() => {
    const pairs = units.map((u) => `${u.id}@${u.version}`).sort()
    return fnv1a(pairs.join('|'))
  })()

  return {
    catalog(): UnitMeta[] {
      return units.map(({ id, type, title, summary }) => ({ id, type, title, summary }))
    },

    get(ids: string[]): KnowledgeUnit[] {
      const result: KnowledgeUnit[] = []
      for (const id of ids) {
        const unit = byId.get(id)
        if (unit !== undefined) result.push(unit)
      }
      return result
    },

    byType(type: UnitType): KnowledgeUnit[] {
      return units.filter((u) => u.type === type)
    },

    search(text: string): KnowledgeUnit[] {
      const trimmed = text.trim()
      if (!trimmed) return []
      const needle = trimmed.toLowerCase()
      return units.filter((u) => {
        const haystack = `${u.title} ${u.summary} ${u.keys.join(' ')} ${u.body}`.toLowerCase()
        return haystack.includes(needle)
      })
    },

    version(): string {
      return versionHash
    },
  }
}

// ---------------------------------------------------------------------------
// validateUnits
// ---------------------------------------------------------------------------

export function validateUnits(units: KnowledgeUnit[]): string[] {
  const errors: string[] = []

  // Collect all ids (Set deduplicates) for seeAlso cross-reference checks.
  const allIds = new Set<string>(units.map((u) => u.id))

  // Track seen ids to detect duplicates on the first repeated occurrence.
  const seen = new Set<string>()

  for (const unit of units) {
    // (a) Empty id guard
    if (!unit.id) {
      errors.push('Unit has an empty id')
      continue
    }

    // (b) Duplicate id detection
    if (seen.has(unit.id)) {
      errors.push(`Duplicate id: "${unit.id}"`)
    }
    seen.add(unit.id)

    // (c) Dangling seeAlso references
    if (unit.seeAlso) {
      for (const ref of unit.seeAlso) {
        if (!allIds.has(ref)) {
          errors.push(`Unit "${unit.id}" has dangling seeAlso reference: "${ref}"`)
        }
      }
    }
  }

  return errors
}
